import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App, preloadFor } from './app/App';
import { AuthProvider } from './auth/AuthProvider';
import { GAP_RETRIES, gapRetryDelay, isApiError, isGapError } from './api/client';
import { isAndroidApp } from './lib/platform';
import './styles/global.css';

// The Android app's own look (android-polish ticket 01): a class every app rule starts with, set before the first
// render, and one chunk a browser never requests. Nothing else in the web build changes.
if (isAndroidApp()) {
  document.documentElement.classList.add('android-app');
  void import('./android/boot').then((m) => m.boot());
}

preloadFor(window.location.pathname);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx is a fact about the request, not a blip: retrying only delays the error state.
      // A short server gap (502, 503, 504 or no connection) is ridden out for about 60 s with backoff.
      retry: (count, err) => (isGapError(err) ? count < GAP_RETRIES : count < 2 && !(isApiError(err) && err.status >= 400 && err.status < 500)),
      retryDelay: (count) => gapRetryDelay(count),
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
    mutations: { retry: false },
  },
});

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
